import { Component, EventEmitter, OnInit } from '@angular/core';
import {
  DwButtonCommand,
  DwButtonConfig,
  DwModalConfig,
  DwModalHandler,
  DwModalResult
} from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';
import {
  ReportFilterPresetManageItem,
  ReportFilterPresetManageResult,
  ReportFilterPresetOwnershipType
} from '../../models/report-filter-preset.model';

export interface ReportFilterPresetManageModalData {
  views: ReportFilterPresetManageItem[];
}

interface PresetOwnershipGroup {
  ownershipType: ReportFilterPresetOwnershipType;
  label: string;
}

@Component({
  selector: 'app-report-filter-preset-manage-modal',
  templateUrl: './report-filter-preset-manage-modal.component.html',
  styleUrls: ['./report-filter-preset-manage-modal.component.scss']
})
export class ReportFilterPresetManageModalComponent implements OnInit, DwModalHandler {
  modalConfig: DwModalConfig<ReportFilterPresetManageModalData>;
  closeModal = new EventEmitter<DwModalResult<ReportFilterPresetManageResult>>();

  views: ReportFilterPresetManageItem[] = [];
  initialDefaultPresetId: number;
  readonly groups: PresetOwnershipGroup[] = [
    { ownershipType: ReportFilterPresetOwnershipType.System, label: 'Recommended' },
    { ownershipType: ReportFilterPresetOwnershipType.FlightSchool, label: 'Flight School' },
    { ownershipType: ReportFilterPresetOwnershipType.User, label: 'My Views' }
  ];

  ngOnInit(): void {
    this.views = this.clone(this.modalConfig.data?.views || []);
    this.initialDefaultPresetId = this.defaultView?.presetId || null;
    this.groups.forEach(group => this.normalizeOrder(group.ownershipType));
  }

  get dashboardDefaultView(): ReportFilterPresetManageItem {
    return this.views.find(view => view.isDashboardDefault);
  }

  get defaultView(): ReportFilterPresetManageItem {
    return this.views.find(view => view.isDefault && !view.remove) || this.dashboardDefaultView;
  }

  groupViews(ownershipType: ReportFilterPresetOwnershipType): ReportFilterPresetManageItem[] {
    return this.views
      .filter(view => view.ownershipType === ownershipType)
      .sort((left, right) => Number(!!left.remove) - Number(!!right.remove)
        || left.sortOrder - right.sortOrder
        || left.name.localeCompare(right.name));
  }

  activeGroupViews(ownershipType: ReportFilterPresetOwnershipType): ReportFilterPresetManageItem[] {
    return this.groupViews(ownershipType).filter(view => !view.remove);
  }

  removedGroupViews(ownershipType: ReportFilterPresetOwnershipType): ReportFilterPresetManageItem[] {
    return this.groupViews(ownershipType).filter(view => view.remove);
  }

  canManageGroup(ownershipType: ReportFilterPresetOwnershipType): boolean {
    const activeViews = this.activeGroupViews(ownershipType);
    return !!activeViews.length && activeViews.every(view => view.canManage);
  }

  onViewsOrdered(views: ReportFilterPresetManageItem[]): void {
    (views || []).forEach((view, index) => view.sortOrder = (index + 1) * 10);
  }

  onViewRemoved(view: ReportFilterPresetManageItem): void {
    this.removeView(view);
  }

  removeView(view: ReportFilterPresetManageItem): void {
    if (!view.canManage || view.isDashboardDefault) {
      return;
    }
    if (view.isDefault) {
      this.setDefaultView(this.dashboardDefaultView);
    }
    view.remove = true;
    this.normalizeOrder(view.ownershipType);
  }

  restoreView(view: ReportFilterPresetManageItem): void {
    if (!view.canManage || view.isDashboardDefault) {
      return;
    }
    view.remove = false;
    this.normalizeOrder(view.ownershipType);
  }

  trackView(_index: number, view: ReportFilterPresetManageItem): number {
    return view.isDashboardDefault ? -1 : view.presetId;
  }

  setDefaultView(view: ReportFilterPresetManageItem): void {
    if (!view || view.remove || view.isDefault) {
      return;
    }
    this.views.forEach(item => item.isDefault = item === view);
  }

  buttonClick(button: DwButtonConfig): Observable<DwModalResult<ReportFilterPresetManageResult>> {
    if (button.command !== DwButtonCommand.saveAndClose) {
      return of({ cancelled: true, closeModal: true, buttonCommand: button.command } as any);
    }

    const orders = this.groups.reduce((result, group) => {
      this.activeGroupViews(group.ownershipType).forEach((view, index) => result.push({
        presetId: view.presetId,
        sortOrder: (index + 1) * 10
      }));
      return result;
    }, [] as Array<{ presetId: number; sortOrder: number }>);

    return of({
      cancelled: false,
      closeModal: true,
      buttonCommand: button.command,
      data: {
        orders,
        removedPresetIds: this.views.filter(view => view.remove && !!view.presetId).map(view => view.presetId),
        defaultPresetId: this.defaultView?.presetId || null,
        defaultChanged: (this.defaultView?.presetId || null) !== this.initialDefaultPresetId
      }
    } as DwModalResult<ReportFilterPresetManageResult>);
  }

  private normalizeOrder(ownershipType: ReportFilterPresetOwnershipType): void {
    this.activeGroupViews(ownershipType)
      .forEach((view, index) => view.sortOrder = (index + 1) * 10);
  }

  private clone<T>(value: T): T {
    return value ? JSON.parse(JSON.stringify(value)) : value;
  }
}
